import React, {useMemo} from 'react'
import {Box, BoxProps, Flex, Stack, Text} from '../../../vanilla'
import {ChevronRightIcon} from '../../icons'
import {ProgressCircleIcon} from '../../icons/progress-circle-icon'
import {ModalNavigationProps} from '../../../types/ui'
import {Promotion, PromotionalBadgeText} from '../../../store/ProductStore'
import {usePromotion} from '../../../store/hooks/usePromotion'
import {observer} from 'mobx-react-lite'
import {useGlobalStore, useBasketStore} from '../../../store/hooks/useStore'
import {ReactElement} from 'react-markdown/lib/react-markdown'
import {baseThemeContract} from '../../../vanilla/vars.css'
import {
  nearPromoContainer,
  promotionDetailsContainerCollapsedBorder,
  promotionDetailsMessagingContainer
} from './styles.css'
import {useBookDeliveryPopover} from '../../../contexts'
import useNavigation from '../../../hooks/use-navigation'
import {useSelectContentTracking} from '../../../analytics/select-content-tracking'
import cn from 'classnames'
import {formatPrice} from '../../../utils/utils'
interface NearPromoProgress {
  progress: number
  total: number
}
interface NearPromoItemProps extends BoxProps<'div'> {
  promotion?: Promotion | null
  bundle?: PromotionalBadgeText | null
  disableInteraction?: boolean
  children?: ReactElement
  onNavigate?: (url: string) => void
  search?: boolean
  cartDrawer?: boolean
  modalNavigationProps?: ModalNavigationProps
  hitId?: string
  displayBadgeText?: boolean
  promoMsg?: string
  multibuyUnitPrice?: string
  unitPriceMsg?: string
}

export const NearPromoItem = observer((props: NearPromoItemProps) => {
  const {
    promotion,
    bundle,
    hitId = '',
    disableInteraction,
    children,
    onNavigate,
    search,
    cartDrawer,
    modalNavigationProps,
    displayBadgeText = false,
    promoMsg,
    multibuyUnitPrice,
    unitPriceMsg,
    ...rest
  } = props
  const sendSelectContentData = useSelectContentTracking()
  const {setIsPromoModalOpen} = useGlobalStore()
  const basketStore = useBasketStore()
  const {PopoverWrapperComponent, hideBookDeliveryPopover} = useBookDeliveryPopover()
  const navigate = typeof onNavigate === 'function' ? onNavigate : useNavigation()

  // Determine type of promotion
  const promo = usePromotion(promotion?.ID)

  const isMultibuy =
    promotion?.t === 'BuyXForTotal' && promo?.productIds && promo?.productIds?.length > 0
  const isMixAndMatch = isMultibuy && promo?.productIds?.length > 1
  const multibuyMessage = isMultibuy ? (isMixAndMatch ? 'Mix & Match' : 'Multibuy') : ''
  const isBuyXGetY =
    promotion?.t === 'BuyXGetY' &&
    promotion?.buyXReq !== undefined &&
    promotion?.getYQuantity !== undefined
  const isMealDealLanding = promo?.c_isMealDealLanding
  const mealDealUrl =
    promotion && isMealDealLanding ? encodeURI(`/meal-deals?promo=${promotion.ID}`) : ''
  const {currency} = useGlobalStore()
  const isOrderLevelPromotion = promotion?.t && promotion.t.indexOf('order-') > -1
  const isLadderDeal = promotion?.ladderDeals && promotion.ladderDeals.length > 0

  if (!promo && !bundle) return null

  const splitOfferMessage = (message: string) => {
    const [amount, price] = message.split('for') || []

    return (
      <>
        {amount} <span style={{fontWeight: '400', fontSize: '12px'}}>for</span> {price}
      </>
    )
  }

  const getNearPromoText = () => {
    // First check for promotion msg
    if (promoMsg) {
      if (promoMsg.includes('for')) {
        return splitOfferMessage(promoMsg)
      } else {
        return promoMsg
      }
    }
    // If no promotion msg, check for bundle msg
    if (bundle?.msg) {
      return bundle.msg
    }
    // Order level promotions have their own format
    if (isOrderLevelPromotion && promo?.save && promo?.spend) {
      return (
        <Text variant="text5" textAlign="left" lineHeight="none" marginBottom="0px">
          Spend {formatPrice(Number(promo.spend), currency)} & get{' '}
          {formatPrice(Number(promo.save), currency)} off
        </Text>
      )
    }
    // Fallback text
    return 'Offer'
  }

  // Work out the unit price message for multibuy and buy x get y promotions
  const multiBuyUnitPriceMessage = isMultibuy ? (
    <Flex gap="4px" align="center">
      <Text as="span" variant="text0" color="black" lineHeight="none" marginBottom="2px">
        {multibuyMessage} from
      </Text>
      <Text as="span" variant="text2Bold" color="black" lineHeight="none" marginBottom="2px">
        {formatPrice(Number(multibuyUnitPrice), currency)}
      </Text>
      <Text as="span" variant="text0" color="black" lineHeight="none" marginBottom="2px">
        each
      </Text>
    </Flex>
  ) : isBuyXGetY && promo?.discountName === 'bogo' ? (
    <Flex gap="4px" align="baseline">
      <Text as="span" variant="text0" color="black" lineHeight="none" marginBottom="2px">
        Cheapest Free
      </Text>
    </Flex>
  ) : isOrderLevelPromotion ? (
    <Flex gap="4px" align="baseline">
      <Text as="span" variant="text0" color="black" lineHeight="none" marginBottom="2px">
        Only available on{' '}
        {promo?.brands && promo?.brands.length === 1
          ? promo.brands[0]
          : promo?.categoryIds && promo?.categoryLabels && promo?.categoryIds.length === 1
          ? promo.categoryLabels[0]
          : 'selected products'}
      </Text>
    </Flex>
  ) : null

  // Work out whether to collapse the borders of the promo container, based on whether the
  // second line will be output.
  const nearPromoContainerStyle = cn(
    nearPromoContainer,
    displayBadgeText && multiBuyUnitPriceMessage
      ? promotionDetailsContainerCollapsedBorder
      : undefined
  )

  const nearPromoProgress = useMemo((): NearPromoProgress | null => {
    if (!promotion || !promo || (!isMultibuy && !isBuyXGetY) || isLadderDeal) {
      return null
    }

    // Grab the informationfor the nearness messaging
    const matchedPromotion = basketStore.missedOffers?.promotions[promotion.ID]
    const completedPromotion = basketStore.missedOffers?.completedPromos?.[promotion.ID]
    const remainingQty = matchedPromotion?.remainingQty && matchedPromotion.remainingQty > 0
      ? matchedPromotion.remainingQty
      : completedPromotion?.remainingQty && completedPromotion.remainingQty > 0
      ? completedPromotion.remainingQty
      : 0
    const matchedRequiredQty = matchedPromotion?.groups?.[0]?.requiredQty
    const completedRequiredQty = completedPromotion?.groups?.[0]?.requiredQty
    const requiredQty = matchedRequiredQty && matchedRequiredQty > 0
      ? matchedRequiredQty
      : completedRequiredQty && completedRequiredQty > 0
      ? completedRequiredQty
      : undefined

    // If the promotion is completed, we return a progress of 1/1 to show the checkmark
    if (completedPromotion && !matchedPromotion && remainingQty === 0) {
      return {
        progress: 1,
        total: 1
      }
    }

    // If missing any required information, we return null
    if (!requiredQty || !remainingQty) {
      return null
    }

    // Else return progress and total for the progress circle
    return {
      progress: requiredQty - remainingQty,
      total: requiredQty
    }
  }, [basketStore.missedOffers, promotion, promo])

  const progressIcon = useMemo(() => {

    return nearPromoProgress ? (
      <Stack className="nearnessWheel">
        <ProgressCircleIcon
          width="40px"
          height="40px"
          gap="4px"
          marginLeft="auto"
          progress={nearPromoProgress.progress}
          total={nearPromoProgress.total}
        />
      </Stack>
    ) : null
  }, [isMultibuy, nearPromoProgress])

  return (
    <PopoverWrapperComponent
      hitId={`${hitId}${promo?.id || ''}`}
      onClickOutside={() => hideBookDeliveryPopover()}
      containerStyle={{zIndex: baseThemeContract.zIndex.popover}}
    >
      <Stack
        spacing={'none'}
        cursor={disableInteraction ? 'auto' : 'pointer'}
        display="flex"
        flexDirection="column"
        borderRadius="base"
        paddingY="6px"
        width="full"
        // Only make it clickable if there's a promotion to show
        onClick={
          promo && !disableInteraction
            ? (e: React.MouseEvent | React.TouchEvent) => {
                sendSelectContentData(promo)
                if (isMealDealLanding) {
                  navigate(mealDealUrl)
                } else {
                  e.preventDefault()
                  setIsPromoModalOpen(true, promotion ?? undefined)
                }
              }
            : undefined
        }
        data-promo-id={promotion?.ID}
        {...rest}
      >
        <Box
          color="black"
          display="flex"
          flexDirection="column"
          borderRadius="base"
          paddingY="4px"
          width="full"
          className={children ? '' : nearPromoContainerStyle}
          data-test-selector="promo-main-container"
        >
          {children ? (
            children
          ) : (
            <>
              <Stack
                direction="row"
                flexWrap="wrap"
                justifyItems="center"
                alignItems="center"
                width={search ? 'auto' : 'full'}
                margin={bundle ? 'auto' : undefined}
                justifyContent={bundle ? 'center' : 'flex-start'}
                style={{gap: '2px 4px'}}
                data-test-selector="promo-header-stack-row"
              >
                <Stack
                  direction="column"
                  width="full"
                  data-test-selector="promo-pricing-stack-column"
                  gridRowGap="2px"
                >
                  <Flex flexWrap="nowrap" alignItems="center" justifyContent="space-between" width="full">
                    <Flex
                      flexWrap="wrap"
                      data-test-selector="promo-text-flex-row"
                      gridColumnGap="4px"
                      alignItems="center"
                      width="full"
                    >
                      <Text
                        display="flex"
                        alignItems="center"
                        align="left"
                        variant={search || cartDrawer ? 'text2Bold' : 'text5'}
                        fontSize="inherit"
                        gridColumnGap="2px"
                      >
                        {isBuyXGetY
                          ? `Buy ${Number(promotion.buyXReq)} Get ${Number(
                              promotion.getYQuantity
                            )} FREE`
                          : getNearPromoText()}
                      </Text>
                      {multibuyMessage && (
                        <Text
                          variant="unstyled"
                          style={{fontSize: '12px', fontWeight: '400'}}
                          lineHeight="inherit"
                          textAlign={'left'}
                          alignSelf="center"
                          data-test-selector="promo-multibuy-message"
                        >
                          {multibuyMessage}
                        </Text>
                      )}
                    {unitPriceMsg && (
                      <Text
                        variant="unstyled"
                        style={{fontSize: '12px', fontWeight: '400'}}
                        lineHeight="inherit"
                        textAlign={'left'}
                        alignSelf="center"
                        data-test-selector="promo-view-offer"
                        flexBasis={search ? undefined : 'full'}
                        marginLeft={search ? 'auto' : undefined}
                      >
                        {unitPriceMsg}
                      </Text>
                    )}
                  </Flex>
                  {isMealDealLanding && (
                    <ChevronRightIcon
                      height="14px"
                      width="14px"
                      marginLeft="auto"
                      marginRight="0px"
                      alignSelf="center"
                    />
                  )}
                  {progressIcon}
                  </Flex>
                </Stack>
              </Stack>
            </>
          )}
        </Box>
        {displayBadgeText && multiBuyUnitPriceMessage && (
          <Box className={promotionDetailsMessagingContainer}>
            <Text
              variant="unstyled"
              style={{fontSize: '12px', fontWeight: '400'}}
              display="flex"
              alignItems="center"
              lineHeight="none"
            >
              {multiBuyUnitPriceMessage}
            </Text>
          </Box>
        )}
      </Stack>
    </PopoverWrapperComponent>
  )
})
